import React, { FC, useCallback, useEffect, useRef } from 'react';
import { NavBar, NavBarProps } from '../../../NavBar';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { Category, EventPageFragment, EventPageType, EventType } from '@graphql/generated';
import { NavbarEventPropsInterface } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type { HeaderTitleStyle, NavigationStyle, VerticalAlignment, WebsiteHeaderColors, WebsiteHeaderTextContent } from '@shared/types/WebsiteHeaderSettings.types';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { InlineEditor } from '@shared/components';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { withWindow } from '@shared/utils/withWindow';
import { AnimatedBorder } from '@shared/components/InlineEditor/components/AnimatedBorder/AnimatedBorder';
import { useFitText } from '../../hooks/useFitText';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHeaderStylesForGuest } from '../../hooks/useHeaderStylesForGuest';

export interface EventNameNavForStandaloneHeaderProps {
  navBarVariant: NavBarProps['variant'];
  eventHandle: string;
  pages: ReadonlyArray<EventPageFragment>;
  handleMenuClicked?: (item: string) => void;
  eventProps: NavbarEventPropsInterface;
  currentPageType: Maybe<EventPageType>;
  pageSlug?: string;
}

interface Props extends EventNameNavForStandaloneHeaderProps {
  colors?: WebsiteHeaderColors | undefined;
  headerTextData?: WebsiteHeaderTextContent | undefined;
  eventDisplayName: Maybe<string>;
  isCompactView: boolean;
  handleEditTextClick: () => void;
  isContentEditable: boolean;
  editableContentRef?: React.RefObject<HTMLDivElement>;
  forceRenderViewIn?: 'mobile' | 'desktop';
  scaleValue?: number;
  renderedContainerHeight?: number;
  renderedContainerWidth?: number;
  headerTitleStyle?: HeaderTitleStyle;
  navigationStyle?: NavigationStyle;
}

const typographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE },
];

const computeResponsiveValue = <T,>(defaultValue: T, mobileValue: number, desktopValue: number, forceRenderViewIn?: 'mobile' | 'desktop') => {
  if (forceRenderViewIn === 'mobile') {
    return mobileValue;
  }
  if (forceRenderViewIn === 'desktop') {
    return desktopValue;
  }
  return defaultValue;
};

const DEFAULT_BORDER_RADIUS = 27;
const FULL_BORDER_RADIUS = 9999;
const FULL_BORDER_RADIUS_LINES_THRESHOLD = 1;
const TEXT_MAX_CHAR_LIMIT = 70;
const CONTAINER_TOTAL_MARGIN = 80;

const INPUT_AREA__PADDING_Y = 32;
const INPUT_AREA__PADDING_X_MOBILE = 24;
const INPUT_AREA__PADDING_X_DESKTOP = 40;
const INPUT_AREA__MARGIN_X_MOBILE = 24;
const INPUT_AREA__MARGIN_X_DESKTOP = 64;

const INLINE_WRAPPER_MIN_WIDTH = 180;

const getVerticalPlacement = (alignment: VerticalAlignment, forceRenderViewIn?: 'mobile' | 'desktop') => {
  switch (alignment) {
    case 'top':
      return { top: computeResponsiveValue({ _: 24, sm4: 0, lg: 0 }, 24, 0, forceRenderViewIn) };
    case 'center':
      return {
        top: '50%',
        transform: 'translateY(-50%)',
      };
    case 'bottom':
      return { bottom: computeResponsiveValue({ _: 24, md: 64, lg: 64 }, 24, 64, forceRenderViewIn) };
  }
};

export const EventNameNavForStandaloneHeader: FC<Props> = props => {
  const {
    handleMenuClicked,
    navBarVariant,
    eventHandle,
    eventProps,
    pages,
    eventDisplayName,
    colors,
    headerTextData,
    isCompactView,
    currentPageType,
    pageSlug,
    handleEditTextClick,
    isContentEditable = false,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    renderedContainerHeight,
    headerTitleStyle,
    navigationStyle,
  } = props;

  const navBarRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  // editableContentRef is not always available, so in that case we need to manage the ref ourselves
  const textRefToPass = editableContentRef || textRef;

  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isXsScreen = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'xs' }));

  const [fontSize] = useResponsive({ values: { _: 64, md: 100, lg: 125 } }, 64);
  const [inputAreaMarginX] = useResponsive({ values: { _: INPUT_AREA__MARGIN_X_MOBILE, md: INPUT_AREA__MARGIN_X_DESKTOP } }, INPUT_AREA__MARGIN_X_DESKTOP);
  const [inputAreaPaddingX] = useResponsive({ values: { _: INPUT_AREA__PADDING_X_MOBILE, md: INPUT_AREA__PADDING_X_DESKTOP } }, INPUT_AREA__PADDING_X_MOBILE);

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const responsiveInputAreaMarginX = computeResponsiveValue(inputAreaMarginX, INPUT_AREA__MARGIN_X_MOBILE, INPUT_AREA__MARGIN_X_DESKTOP, forceRenderViewIn);
  const responsiveInputAreaPaddingX = computeResponsiveValue(inputAreaPaddingX, INPUT_AREA__PADDING_X_MOBILE, INPUT_AREA__PADDING_X_DESKTOP, forceRenderViewIn);

  const {
    containerRef,
    fontSize: fitFontSize,
    lineCount: titleLinesCount,
  } = useFitText({
    textRef: textRefToPass,
    maxFontSize: Number.parseFloat(scaleFontSize(pxToRem(computeResponsiveValue(fontSize, 64, 125, forceRenderViewIn)), Category.EVENT_TITLE)) * 16,
    // widthOffset is input area total outer margin + input area total padding
    widthOffset: responsiveInputAreaMarginX * 2 + responsiveInputAreaPaddingX * 2,
    heightOffset: INPUT_AREA__PADDING_Y * 2,
    minFontSize: 10,
  });

  const { eventInfo } = useEventInfo();

  const { tq } = useTranslation('joykit');

  useEffect(() => {
    if (editableContentRef?.current && isContentEditable) {
      editableContentRef.current.focus();
      withWindow(globalWindow => {
        // place caret at end of the text
        const range = globalWindow.document.createRange();
        range.selectNodeContents(editableContentRef.current as Node);
        range.collapse(false);
        const sel = globalWindow.getSelection?.();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    }
  }, [editableContentRef, isContentEditable]);

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  const handleInput = useEventCallback(() => {
    if (!editableContentRef?.current) return;

    const element = editableContentRef.current;
    const textContent = element.innerText;
    if (textContent.length > TEXT_MAX_CHAR_LIMIT) {
      element.innerText = textContent.slice(0, TEXT_MAX_CHAR_LIMIT);
      // Restore caret to end
      withWindow(globalWindow => {
        const range = globalWindow.document.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        const sel = globalWindow.getSelection?.();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    }
  });

  const applyScaling = useCallback((value: number) => value / scaleValue, [scaleValue]);

  // if hasModifiedEventNameOnce is true then show headerTextData.content. In case of hasModifiedEventNameOnce is false or undefined we need to show the eventDisplayName
  const textContent = (headerTextData?.hasModifiedEventNameOnce ? headerTextData.content : eventDisplayName) || '';

  const { navTextColor, navEffectStyles, titleTextColor, titleEffectStyles } = useHeaderStylesForGuest({
    headerTitleStyle,
    navigationStyle,
    colorsData: colors,
  });

  return (
    <>
      <Box
        ref={navBarRef}
        width="100%"
        pointerEvents={isContentEditable ? 'none' : 'unset'}
        position="relative"
        zIndex="docked"
        paddingBottom={isCompactView ? 8 : 0}
      >
        <NavBar
          preventExpandingToFullWidth
          handleMenuClicked={handleMenuClicked}
          title={eventDisplayName}
          variant={navBarVariant}
          data-testid={'header-nav'}
          eventHandle={eventHandle}
          pages={pages}
          eventProps={eventProps}
          textColor={navTextColor}
          {...navEffectStyles}
          borderRadius={navigationStyle ? pxToRem(DEFAULT_BORDER_RADIUS) : undefined}
          paddingX={4}
          paddingY={2}
        />
      </Box>
      {!isCompactView && (
        <Flex
          ref={containerRef}
          position="relative"
          width="100%"
          height={`calc(${renderedContainerHeight ? `${renderedContainerHeight}px` : '100dvh'} - ${navBarRef.current?.clientHeight || 0}px - ${CONTAINER_TOTAL_MARGIN}px)`}
          marginTop={8}
        >
          <Flex
            width="100%"
            maxHeight="100%"
            position="absolute"
            zIndex="docked"
            {...getVerticalPlacement(headerTextData?.pageAlignment?.y || 'center', forceRenderViewIn)}
            justifyContent={headerTextData?.pageAlignment?.x || headerTextData?.textAlignment || 'left'}
            // paddingX here is in the role of the horizontal margin of the input area
            paddingX={responsiveInputAreaMarginX}
          >
            <Box
              maxWidth="100%"
              paddingY={INPUT_AREA__PADDING_Y}
              paddingX={responsiveInputAreaPaddingX}
              {...titleEffectStyles}
              borderRadius={headerTitleStyle ? (titleLinesCount <= FULL_BORDER_RADIUS_LINES_THRESHOLD ? FULL_BORDER_RADIUS : DEFAULT_BORDER_RADIUS) : undefined}
            >
              <InlineEditor
                elementLabel={eventInfo?.eventType === EventType.wedding ? tq`Couple's Name` : tq`Event Display Name`}
                actionData={{
                  edit: handleEditTextClick,
                  font: () => handleEditFontClick(Category.EVENT_TITLE),
                }}
                wrapperCSS={{
                  zIndex: 'banner',
                  minWidth: textContent.trim() ? 'unset' : pxToRem(INLINE_WRAPPER_MIN_WIDTH),
                }}
                inset={isMobile ? { x: -20, y: -24 } : { x: -16, y: -20 }}
                componentName="couplesName"
                pageName={currentPageType}
                pageSlug={pageSlug || ''}
              >
                <GuestSiteTypographyOverride override={typographyOverride}>
                  {isContentEditable ? (
                    <Box position="relative">
                      <AnimatedBorder
                        isVisibile={true}
                        borderWidth={pxToRem(applyScaling(2))}
                        borderRadius={`${applyScaling(8)}px`}
                        inset={isMobile ? { x: '-20px', y: '-24px' } : { x: '-16px', y: '-20px' }}
                        padding={0}
                      />
                      <Box
                        ref={textRefToPass}
                        position="relative"
                        typographyVariant={isXsScreen ? 'display4' : 'display6'}
                        color={titleTextColor}
                        zIndex={11}
                        onInput={handleInput}
                        contentEditable={isContentEditable}
                        dangerouslySetInnerHTML={{ __html: textContent }}
                        textAlign={headerTextData?.textAlignment || 'left'}
                        whiteSpace="pre-wrap"
                        outline="none"
                        maxHeight={`calc(${renderedContainerHeight ? `${renderedContainerHeight}px` : '100dvh'} - ${
                          navBarRef.current?.clientHeight || 0
                        }px - ${CONTAINER_TOTAL_MARGIN}px - ${INPUT_AREA__PADDING_Y * 2}px)`}
                        overflow="hidden"
                        top="0"
                        style={{
                          caretColor: '#4E06F2',
                          fontSize: fitFontSize,
                        }}
                      />
                    </Box>
                  ) : (
                    <TextV2
                      ref={textRefToPass}
                      // The LCP element, large block-level text
                      as="h1"
                      typographyVariant={isXsScreen ? 'display4' : 'display6'}
                      color={titleTextColor}
                      whiteSpace="pre-wrap"
                      textAlign={headerTextData?.textAlignment || 'left'}
                      position="relative"
                      style={{ fontSize: fitFontSize }}
                    >
                      {textContent}
                    </TextV2>
                  )}
                </GuestSiteTypographyOverride>
              </InlineEditor>
            </Box>
          </Flex>
        </Flex>
      )}
    </>
  );
};

EventNameNavForStandaloneHeader.displayName = 'EventNameNavForStandaloneHeader';
